import { Divider, Heading, Skeleton, VStack, HStack, useToast, Link, Select, Spacer, Button, grid } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { SyntheticEvent, useCallback, useState } from "react";
import { components } from "schema/main";
import { client, prependApiUrl } from "utils/api/client";
import "styles/atlas.css";
import { useNavigate } from "react-router-dom";
import { baseToast } from "@diamondlightsource/ui-components";
import { MdDownload } from "react-icons/md";
import { CopyAtlasPathButton } from "./CopyAtlasPathButton";

type Tomogram = components["schemas"]["TomogramResponse"];

export interface SearchMapProps {
  gridSquare?: components["schemas"]["GridSquare"] | null;
  scalingFactor: number;
}

interface TomogramRegion {
  dataCollectionId: number;
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const fetchTomograms = async (searchMapId: number | null, scalingFactor: number) => {
  if (!searchMapId) {
    return null;
  }

  /** If this is a search map, get the list of tomograms instead of foil holes */
  const tomogramReq = await client.safeGet(`grid-squares/${searchMapId}/tomograms?limit=3000`);

  if (tomogramReq.status !== 200) {
    return null;
  }

  return tomogramReq.data.items.reduce((filtered: TomogramRegion[], tomogram: Tomogram) => {
    if (
      !tomogram.pixelLocationX ||
      !tomogram.pixelLocationY ||
      !tomogram.sizeX ||
      !tomogram.sizeY ||
      !tomogram.pixelSpacing
    ) {
      return filtered;
    }

    filtered.push({
      dataCollectionId: tomogram.dataCollectionId,
      id: tomogram.tomogramId,
      // Binning and pixel size conversion from atlas to search map
      x: tomogram.pixelLocationX,
      y: tomogram.pixelLocationY,
      // Apply tomogram pixel size conversion and scaling factor
      width: tomogram.sizeX * tomogram.pixelSpacing * scalingFactor,
      height: tomogram.sizeY * tomogram.pixelSpacing * scalingFactor,
    });

    return filtered;
  }, []) as TomogramRegion[];
};

export const SearchMap = ({ gridSquare, scalingFactor }: SearchMapProps) => {
  const [viewBox, setViewBox] = useState("0 0 512 512");
  const { data, isLoading } = useQuery({
    queryKey: ["searchMapTomograms", gridSquare?.gridSquareId],
    queryFn: async () => await fetchTomograms(gridSquare?.gridSquareId!, scalingFactor),
  });
  const [enhanced, setEnhanced] = useState("false");

  const navigate = useNavigate();
  const toast = useToast();

  const handleEnhancedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEnhanced(e.target.value);
  };

  const handleItemClicked = useCallback(
    async (tomogram: TomogramRegion) => {
      const response = await client.safeGet(`dataCollections/${tomogram.dataCollectionId}`);

      if (response.status !== 200) {
        toast({
          ...baseToast,
          title: "Error",
          description: "Could not get tomogram information",
          status: "error",
        });
        return;
      }

      const dataCollection: components["schemas"]["BaseDataCollectionOut"] = response.data;

      navigate(`../tomograms/${dataCollection.index}`, { relative: "path" });
    },
    [navigate, toast],
  );

  const handleLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      setViewBox(`0 0 ${e.currentTarget.naturalWidth} ${e.currentTarget.naturalHeight}`);
    },
    [setViewBox],
  );

  const imageUrl = prependApiUrl(`grid-squares/${gridSquare?.gridSquareId}/image?isEnhanced=${enhanced}`);

  return (
    <VStack
      display='flex'
      flex='1 0 300px'
      p='0.5em'
      bg='diamond.50'
      alignItems='start'
      border='1px solid'
      borderColor='diamond.900'
    >
      <HStack w='100%'>
        <Heading>Search Map</Heading>
        <CopyAtlasPathButton baseImagePath={gridSquare?.image ?? null} />
        <Spacer />
        {gridSquare?.gridSquareId && (
          <>
            <Button
              as={Link}
              leftIcon={<MdDownload />}
              href={prependApiUrl(`grid-squares/${gridSquare?.gridSquareId}/image?isEnhanced=true&isPng=true`)}
              target='_blank'
            >
              Download
            </Button>
            <Select value={enhanced} onChange={handleEnhancedChange} w='20%' minW='200px'>
              <option value='false'>Raw</option>
              <option value='true'>Enhanced</option>
            </Select>
          </>
        )}
      </HStack>

      <Divider />
      {!gridSquare?.gridSquareId ? (
        <Heading w='100%' variant='notFound' size='md' h='512px' alignContent='center'>
          No search map selected. Select one by clicking one of the squares on the atlas.
        </Heading>
      ) : data === undefined || isLoading ? (
        <Skeleton h='512px' w='100%' />
      ) : data === null ? (
        <Heading variant='notFound' size='md' h='512px' w='100%' alignContent='center'>
          No tomograms available
        </Heading>
      ) : (
        <div style={{ width: "100%" }} className='img-wrapper'>
          <img src={imageUrl} alt='Search Map' onLoad={handleLoad} />
          <svg viewBox={viewBox} className='static-png'>
            {data.map((item, i) => (
              <rect
                data-testid={`item-${i}`}
                key={i}
                x={item.x - item.width / 2}
                y={item.y - item.height / 2}
                width={item.width}
                height={item.height}
                onClick={() => handleItemClicked(item)}
                role='button'
                stroke='green'
                fill='green'
                fillOpacity='0.4'
                cursor='pointer'
              />
            ))}
          </svg>
        </div>
      )}
    </VStack>
  );
};
